// ═══════════════════════════════════════════════════════════════
// familyApi.js — Client /api/projects/:id/family/* (Module Family P1 + P2)
// ═══════════════════════════════════════════════════════════════
// P1 : household/persons/relationships/actions-pending/relation-types/transfer.
// P2 (migration 067) : graph / cohabitations / doc-categories /
//                       document-routing / canvas / admin2.
// Pattern emprunté à bookApi.js.

const getToken = () => localStorage.getItem('oracle_token');

export class FamilyApiError extends Error {
    constructor(status, code, message, detail) {
        super(message || code || `HTTP ${status}`);
        this.name = 'FamilyApiError';
        this.status = status;
        this.code = code;
        this.detail = detail;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new FamilyApiError(
                res.status,
                data.error,
                data.message || data.error || `HTTP ${res.status}`,
                data
            );
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

const root = (pid) => `/api/projects/${pid}/family`;

// ─── P1 (inchangés) ─────────────────────────────────────────────
export const initializeFamily = (pid, name = null) =>
    request(`${root(pid)}/initialize`, { method: 'POST', body: { name } });

export const getHousehold = (pid) => request(`${root(pid)}/household`);

export const createPerson = (pid, person) =>
    request(`${root(pid)}/persons`, { method: 'POST', body: person });

export const updatePerson = (pid, personId, patch) =>
    request(`${root(pid)}/persons/${personId}`, { method: 'PATCH', body: patch });

export const deletePerson = (pid, personId) =>
    request(`${root(pid)}/persons/${personId}`, { method: 'DELETE' });

export const createRelationship = (pid, rel) =>
    request(`${root(pid)}/relationships`, { method: 'POST', body: rel });

export const deleteRelationship = (pid, relId) =>
    request(`${root(pid)}/relationships/${relId}`, { method: 'DELETE' });

export const listActionsPending = (pid, { status = null, dueBefore = null } = {}) => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (dueBefore) qs.set('due_before', dueBefore);
    const s = qs.toString();
    return request(`${root(pid)}/actions-pending${s ? `?${s}` : ''}`);
};

export const listRelationTypes = (pid) => request(`${root(pid)}/relation-types`);

// ─── P2 — Graph (include CSV + pagination actions) ─────────────
export const getFamilyGraph = (pid, { include = null, actionsLimit = null, actionsOffset = null } = {}) => {
    const qs = new URLSearchParams();
    if (include) qs.set('include', Array.isArray(include) ? include.join(',') : include);
    if (actionsLimit != null) qs.set('actions_limit', String(actionsLimit));
    if (actionsOffset != null) qs.set('actions_offset', String(actionsOffset));
    const s = qs.toString();
    return request(`${root(pid)}/graph${s ? `?${s}` : ''}`);
};

// ─── P2 — Cohabitations ────────────────────────────────────────
export const createCohabitation = (pid, { label, isPrimary = false }) =>
    request(`${root(pid)}/cohabitations`, { method: 'POST', body: { label, is_primary: isPrimary } });

export const updateCohabitation = (pid, coId, patch) =>
    request(`${root(pid)}/cohabitations/${coId}`, { method: 'PATCH', body: patch });

export const deleteCohabitation = (pid, coId) =>
    request(`${root(pid)}/cohabitations/${coId}`, { method: 'DELETE' });

export const addCohabitationMember = (pid, coId, { personId, role = null, shareRatio = null }) =>
    request(`${root(pid)}/cohabitations/${coId}/members`, {
        method: 'POST',
        body: { person_id: personId, role_in_cohabitation: role, share_ratio: shareRatio },
    });

export const removeCohabitationMember = (pid, coId, personId) =>
    request(`${root(pid)}/cohabitations/${coId}/members/${personId}`, { method: 'DELETE' });

// ─── P2 — Catégories documents ─────────────────────────────────
export const listDocCategories = (pid) => request(`${root(pid)}/doc-categories`);

export const createCustomDocCategory = (pid, { code, label, icon = null, color = null, sortOrder = 0 }) =>
    request(`${root(pid)}/doc-categories`, {
        method: 'POST',
        body: { code, label, icon, color, sort_order: sortOrder },
    });

export const updateDocumentRouting = (pid, routingId, { categoryId = null, nationalityTag = null } = {}) => {
    const body = {};
    if (categoryId !== null) body.category_id = categoryId;
    if (nationalityTag !== null) body.nationality_tag = nationalityTag;
    return request(`${root(pid)}/document-routing/${routingId}`, { method: 'PATCH', body });
};

// ─── P2 — Canvas ───────────────────────────────────────────────
export const getCanvas = (pid) => request(`${root(pid)}/canvas`);

export const upsertCanvas = (pid, patch) =>
    request(`${root(pid)}/canvas`, { method: 'PUT', body: patch });

// ─── P2 — Admin2 ───────────────────────────────────────────────
export const promoteAdmin2 = (pid, personId) =>
    request(`${root(pid)}/persons/${personId}/promote-admin2`, { method: 'POST', body: {} });

export const demoteAdmin2 = (pid, personId) =>
    request(`${root(pid)}/persons/${personId}/demote-admin2`, { method: 'POST', body: {} });
